import { FormEvent, useMemo, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/Buttons/Button';
import PasswordInput from 'src/components/FormComponents/PasswordInput';
import { CodeStep } from 'src/components/AccountAccess/CodeStep';
import { ContactInput } from 'src/components/AccountAccess/ContactInput';
import {
    AccessSession,
    AccessTokens,
    Contact,
    accessErrorCode,
    accessErrorMessage,
} from 'src/components/AccountAccess/accessErrors';
import { LandingPageHeader } from 'src/view/screens/Mundu/components';
import { Screen } from 'src/view/routes/Router';
import container from 'src/container';
import { useNotification } from 'src/components/Notification/useNotfication';

const {
    cradle: { authService },
} = container;

/** Login identifiers that look like a contact prefill the first step. */
export type ActivateAccountState = {
    identifier?: string;
    /** Continues on the code step, e.g. after login sent a new invitation code. */
    session?: AccessSession;
    notice?: 'codeExpiredResent';
};

const contactFromIdentifier = (identifier = ''): Contact => {
    const compact = identifier.replace(/\s/g, '');
    return /^(?:\+47)?\d{8}$/.test(compact)
        ? { kind: 'phone', value: compact }
        : { kind: 'email', value: identifier.includes('@') ? identifier : '' };
};

const isStrongPassword = (password: string) =>
    password.length >= 8 &&
    /[a-z]/.test(password) &&
    /[A-Z]/.test(password) &&
    /\d/.test(password);

/** Invitation activation: confirm a code sent to the invited contact, then choose a password. */
export const ActivateAccount = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const notify = useNotification();
    const location = useLocation<ActivateAccountState | undefined>();
    const [contact, setContact] = useState<Contact>(() =>
        contactFromIdentifier(location.state?.identifier),
    );
    const [session, setSession] = useState<AccessSession | undefined>(
        location.state?.session,
    );
    const [verified, setVerified] = useState(false);
    const [password, setPassword] = useState('');
    const [repeat, setRepeat] = useState('');
    const [error, setError] = useState<string>();
    const [accountActive, setAccountActive] = useState(false);
    const [busy, setBusy] = useState(false);

    const headerColors = useMemo(
        () => ({
            textColor: 'woodyBrown',
            activeTextColor: 'mongooseGray',
            backgroundColor: 'lightPeach',
        }),
        [],
    );

    const run = async (action: () => Promise<void>) => {
        setBusy(true);
        setError(undefined);
        setAccountActive(false);
        try {
            await action();
        } catch (cause) {
            setAccountActive(accessErrorCode(cause) === 'ACCOUNT_ACTIVE');
            setError(accessErrorMessage(cause, t));
        } finally {
            setBusy(false);
        }
    };

    const requestCode = (event: FormEvent) => {
        event.preventDefault();
        void run(async () => {
            setSession(
                await authService.accountAccess<AccessSession>('start', {
                    purpose: 'invitation',
                    ...contact,
                }),
            );
        });
    };

    const verifyCode = async (code: string) => {
        await authService.accountAccess('verify', {
            session: session?.session,
            code,
        });
        setVerified(true);
    };

    const choosePassword = (event: FormEvent) => {
        event.preventDefault();
        if (!isStrongPassword(password))
            return setError(t('accountAccess.passwordRules'));
        if (password !== repeat)
            return setError(t('validation.passwordsMismatch'));
        void run(async () => {
            try {
                const tokens = await authService.accountAccess<AccessTokens>(
                    'finish',
                    { session: session?.session, password },
                );
                authService.acceptAccessTokens(tokens);
            } catch (cause) {
                if (accessErrorCode(cause) !== 'SIGN_IN_REQUIRED') throw cause;
                // The password is saved; only the automatic sign-in failed.
                notify(t('accountAccess.errors.SIGN_IN_REQUIRED'));
            }
            // The login page loads the signed-in user and redirects home.
            history.replace('/login');
        });
    };

    const step = !session ? 'contact' : !verified ? 'code' : 'password';

    return (
        <div className="bg-fixed min-h-screen flex flex-col">
            <LandingPageHeader
                colors={headerColors}
                scrollItems={false}
                navItems={[{ id: Screen.Mundu, name: t('nav.home') }]}
                languageSelect
            />
            <div className="grow flex justify-center items-center px-6 py-10">
                <div className="flex flex-col gap-6 max-w-lg w-full">
                    <div className="flex flex-col gap-2 text-center">
                        <h1 className="font-bold text-2xl text-woodyBrown">
                            {t('accountAccess.activation.title')}
                        </h1>
                        <p className="text-mongooseGray text-base">
                            {t('accountAccess.activation.description')}
                        </p>
                    </div>
                    {location.state?.notice && session && (
                        <p
                            role="status"
                            className="rounded-xl bg-lightPeach/60 p-4 text-sm text-woodyBrown"
                        >
                            {t(
                                `accountAccess.activation.${location.state.notice}`,
                            )}
                        </p>
                    )}
                    <div className="p-6 rounded-2xl bg-starkWhite/80 shadow-sm flex flex-col gap-4 text-woodyBrown">
                        <div className="font-semibold">
                            {t(`accountAccess.activation.steps.${step}`)}
                        </div>
                        {step === 'contact' && (
                            <form
                                className="flex flex-col gap-4"
                                onSubmit={requestCode}
                            >
                                <ContactInput
                                    contact={contact}
                                    onChange={setContact}
                                    disabled={busy}
                                    error={error}
                                />
                                {accountActive && (
                                    <Link
                                        to="/reset-password"
                                        className="text-sm underline underline-offset-2 self-start"
                                    >
                                        {t('login.forgotPassword')}
                                    </Link>
                                )}
                                <div className="flex justify-center">
                                    <Button
                                        submit
                                        disabled={busy || !contact.value.trim()}
                                        title={t('accountAccess.sendCode')}
                                    />
                                </div>
                            </form>
                        )}
                        {step === 'code' && session && (
                            <CodeStep
                                session={session}
                                submitLabel={t('accountAccess.confirmCode')}
                                onSubmit={verifyCode}
                            >
                                <button
                                    type="button"
                                    className="underline underline-offset-2"
                                    onClick={() => setSession(undefined)}
                                >
                                    {t('accountAccess.useDifferentContact')}
                                </button>
                            </CodeStep>
                        )}
                        {step === 'password' && (
                            <form
                                className="flex flex-col gap-4"
                                onSubmit={choosePassword}
                            >
                                <p className="text-sm text-mongooseGray">
                                    {t('accountAccess.passwordRules')}
                                </p>
                                <PasswordInput
                                    className="text-input"
                                    label={t(
                                        'resetPassword.reset.newPasswordLabel',
                                    )}
                                    autoComplete="new-password"
                                    value={password}
                                    disabled={busy}
                                    onChange={event =>
                                        setPassword(event.target.value)
                                    }
                                />
                                <PasswordInput
                                    className="text-input"
                                    label={t(
                                        'resetPassword.reset.confirmPasswordLabel',
                                    )}
                                    autoComplete="new-password"
                                    value={repeat}
                                    disabled={busy}
                                    onChange={event =>
                                        setRepeat(event.target.value)
                                    }
                                />
                                {error && (
                                    <p
                                        role="alert"
                                        className="text-sm text-venetianRed"
                                    >
                                        {error}
                                    </p>
                                )}
                                <div className="flex justify-center">
                                    <Button
                                        submit
                                        disabled={busy || !password || !repeat}
                                        title={
                                            busy
                                                ? t('login.submitting')
                                                : t(
                                                      'accountAccess.activation.submit',
                                                  )
                                        }
                                    />
                                </div>
                            </form>
                        )}
                    </div>
                    <div className="text-center">
                        <Link
                            to="/login"
                            className="text-sm text-woodyBrown underline underline-offset-2"
                        >
                            {t('resetPassword.backToLogin')}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};
